import React, { ReactNode, useEffect, useRef, useState } from 'react'

export interface ActionMenuItem {
  label: string
  icon?: ReactNode
  onClick?: (param?: any) => void
  className?: string
  color?: string
}
export type TriggerType = 'click' | 'hover'

export interface ActionMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  actions: ActionMenuItem[]
  triggerIcon?: ReactNode
  actionParam?: any
  triggerMode?: TriggerType
}

export function ActionMenu (props: ActionMenuProps): React.JSX.Element {
  const {
    actions,
    triggerIcon,
    actionParam,
    triggerMode = 'hover',
    className,
    ...passProps
  } = props

  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (triggerMode !== 'click') return

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [triggerMode])

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (triggerMode === 'click') {
      setIsOpen((prev) => !prev)
    }
  }

  const handleMouseEnter = () => {
    if (triggerMode === 'hover') setIsOpen(true)
  }

  const handleMouseLeave = () => {
    if (triggerMode === 'hover') setIsOpen(false)
  }

  const handleActionClick = (e: React.MouseEvent, callback?: (param?: any) => void) => {
    e.stopPropagation()
    setIsOpen(false)
    if (callback) {
      callback(actionParam)
    }
  }

  const actionItems = actions.map((action, index) => (
    <button
      key={`${action.label}-${index}`}
      className={action.className || 'action-menu-item'}
      onClick={(e) => handleActionClick(e, action.onClick)}
      style={{ color: action.color }}
    >
      {action.icon && <span className='action-menu-icon'>{action.icon}</span>}
      {action.label}
    </button>
  ))

  return (
    <div
      className={className || 'action-menu-container'}
      ref={menuRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...passProps}
    >
      <button
        className='action-menu-trigger'
        onClick={handleTriggerClick}
        aria-haspopup='true'
        aria-expanded={isOpen}
      >
        {triggerIcon || <span>⋮</span>}
      </button>

      <div className={`action-menu-dropdown ${isOpen ? 'is-open' : ''}`}>
        {actionItems}
      </div>
    </div>
  )
}
